/**
 * Response DTOs for the owner API (docs/ARCHITECTURE.md §10). Request bodies and embedded payloads come from
 * @contentops/contract; the envelopes below are the web app's expectations of the API where §10 does not pin a shape.
 * Every schema is lenient on extra fields (zod strips them) and strict on the fields the UI relies on, so a drift shows
 * up as a clear "bad_response" error instead of a blank screen.
 */
import {
  AccountPublishingImpact, NotificationList, NotificationSettings as NotificationSettingsDto, NotificationView, PublicationHistory as PublicationHistoryDto,
  PublicationsPage as PublicationsPageDto, PublishingSettings as PublishingSettingsDto, PushPublicKey, PushTestResult as PushTestResultDto, RescheduleResponse,
  AccountView, DecisionType, FeedbackVerdict, LearningProposalView, PublicationView, PublishPlanResponse, QualityReport, QueueView, SeriesPlan, SessionState, Stage, StudioSettings as StudioSettingsDto,
  PlaylistSyncResult as PlaylistSyncResultDto, SeriesPlaylists as SeriesPlaylistsDto, VersionCovers as VersionCoversDto, SeriesCovers as SeriesCoversDto,
} from "@contentops/contract";
import { z } from "zod";

const Id = z.string().min(1);
const Time = z.string();
const nul = <T extends z.ZodType>(t: T) => t.nullable().optional();

// ───────── identity ─────────
export const User = z.object({ id: Id, email: z.string(), name: z.string().nullable().optional(), role: z.enum(["owner", "reviewer"]).default("owner") });
export const MeResponse = z.object({ user: User });

// ───────── health & dashboard (§11) ─────────
export const Usage = z.object({
  five_hour_pct: nul(z.number()), seven_day_pct: nul(z.number()), resets_at: nul(Time), limited: z.boolean().default(false),
});
export const RunnerStatus = z.object({
  name: z.string(), last_seen_at: nul(Time), online: z.boolean().optional(),
  usage: nul(Usage), active_sessions: nul(z.number()), max_sessions: nul(z.number()),
});
const ATTENTION_KINDS = [
  "decision", "publication_failed", "publication_auth_required", "publication_slot_missed", "publication_blocked", "publication_not_live", "publication_waiting",
  "publishing_paused", "work_item_escalated", "account_auth", "learning_proposed", "other",
] as const;
type AttentionKind = (typeof ATTENTION_KINDS)[number];
export const AttentionItem = z.object({
  // Known kinds pass through; an unknown publication-* kind (a newer server) still routes to the Publish screen as a failure.
  kind: z.string().catch("other").transform((k): AttentionKind => (ATTENTION_KINDS as readonly string[]).includes(k) ? k as AttentionKind : k.startsWith("publication") ? "publication_failed" : "other"),
  id: Id, title: z.string(), detail: nul(z.string()), series_id: nul(Id), version_id: nul(Id), at: nul(Time), blocking: z.boolean().optional(),
});
export const Dashboard = z.object({
  needs_attention: z.object({
    total: z.number(), items: z.array(AttentionItem).default([]),
    // M2.1 publishing hardening counts
    blocked_publications: z.number().default(0), not_live_publications: z.number().default(0), long_waiting_publications: z.number().default(0),
    pending_dedupe_checks: z.number().default(0), publishing_paused: z.boolean().default(false),
  }),
  awaiting_review: z.number(),
  plans_awaiting_selection: z.number(),
  generating: z.number(),
  scheduled_7d: z.number(),
  published_today: z.number(),
  comments_needing_attention: z.number(),
  learnings_pending: z.number().default(0),   // M1.5: learning proposals waiting for the owner
  active_series: z.number().optional(),
  runner: nul(RunnerStatus),
  runway: z.array(z.object({ queue_id: Id, name: z.string(), scheduled: z.number(), inventory: z.number().optional(), days: nul(z.number()) })).default([]),
});
export const Health = z.object({ ok: z.boolean(), version: z.string().optional(), db: z.unknown().optional(), runners: z.array(z.object({ name: z.string(), last_seen_at: nul(Time) })).default([]) });

// ───────── taxonomy (assumed endpoints: GET /api/categories, GET /api/languages) ─────────
export const Category = z.object({ id: Id, slug: z.string(), name: z.string(), enabled: z.boolean().default(true), default_languages: z.array(z.string()).default(["en"]) });
export const Language = z.object({ code: z.string(), name: z.string(), direction: z.enum(["ltr", "rtl"]).default("ltr"), readiness: z.enum(["piloting", "ready", "disabled"]).default("ready") });
export const CategoriesResponse = z.object({ categories: z.array(Category) });
export const LanguagesResponse = z.object({ languages: z.array(Language) });

// ───────── jobs & uploads ─────────
export const UploadResponse = z.object({ artifact_id: Id });
export const CreateJobResponse = z.object({ job_id: Id, series_id: nul(Id) });

// ───────── series ─────────
export const SeriesStatus = z.enum(["planning", "awaiting_selection", "building", "in_review", "approved", "publishing", "complete", "cancelled"]);
export const SeriesSummary = z.object({
  id: Id, slug: z.string().optional(), title: z.string(), category: z.string(), status: SeriesStatus,
  priority: z.enum(["normal", "urgent"]).default("normal"),
  session_state: SessionState.default("unopened"),
  stage: nul(Stage.or(z.string())), stage_detail: nul(z.string()), progress_pct: nul(z.number()),
  episodes_total: z.number().default(0), episodes_approved: z.number().default(0), episodes_in_review: z.number().default(0),
  open_decisions: z.number().default(0), usage_resets_at: nul(Time),
  updated_at: nul(Time),
});
export const SeriesListResponse = z.object({ series: z.array(SeriesSummary) });

export const DecisionOption = z.object({ id: z.string(), label: z.string(), description: z.string().optional() });
export const DecisionAttachment = z.union([
  z.string().transform((artifact_id) => ({ artifact_id, kind: "other" as string, filename: null as string | null })),
  z.object({ artifact_id: Id, kind: z.string().default("other"), filename: z.string().nullable().default(null) }),
]);
export const Decision = z.object({
  id: Id, series_id: nul(Id), version_id: nul(Id), episode_sequence: nul(z.number()),
  type: DecisionType, prompt: z.string(), detail: nul(z.string()),
  options: z.array(DecisionOption).default([]),
  attachments: z.array(DecisionAttachment).default([]),
  blocking: z.boolean().default(true),
  default_answer: nul(z.object({ option_id: z.string().optional(), text: z.string().optional() })),
  status: z.enum(["open", "answered", "expired", "cancelled"]),
  answer: nul(z.object({ option_id: z.string().optional(), text: z.string().optional() })),
  created_at: Time, answered_at: nul(Time),
});
export const DecisionsResponse = z.object({ decisions: z.array(Decision) });

export const VersionStatus = z.enum(["building", "in_review", "changes_requested", "approved", "rejected", "stale", "superseded"]);
export const VersionRef = z.object({
  id: Id, language_code: z.string(), role: z.enum(["master", "variant"]), version: z.number(), status: VersionStatus,
  created_at: Time, score: nul(z.number()), origin: z.enum(["studio", "operator_supplied", "imported"]).catch("studio").optional(),
});
export const Episode = z.object({
  id: Id, sequence: z.number(), title: z.string(),
  status: z.enum(["planned", "building", "in_review", "approved", "rejected", "held", "cancelled"]),
  versions: z.array(VersionRef).default([]),
});
export const PlanRecord = z.object({ version: z.number(), status: z.enum(["proposed", "approved", "superseded"]), plan: SeriesPlan, created_at: nul(Time) });
export const SeriesDetail = z.object({
  series: SeriesSummary.extend({
    brand: z.record(z.string(), z.unknown()).default({}),
    languages: z.array(z.string()).default(["en"]),
    source: nul(z.object({ type: z.string(), title: z.string() })),
    git_branch: nul(z.string()),
  }),
  session: z.object({ state: SessionState, session_id: nul(z.string()), last_activity_at: nul(Time) }),
  plan: nul(PlanRecord),
  episodes: z.array(Episode).default([]),
  decisions: z.array(Decision).default([]),
});

// ───────── conversation (§30) ─────────
export const MessageCard = z.object({
  type: z.enum(["plan", "decision", "review", "permission", "question"]),
  ref: z.union([z.string(), z.number()]).transform(String),
  title: z.string().optional(), score: z.number().optional(),
});
export const Message = z.object({
  // An unknown kind (a newer server) degrades to a plain text bubble instead of failing the whole thread.
  id: z.number(), direction: z.enum(["studio", "owner", "system"]), kind: z.enum(["text", "card", "activity"]).catch("text"),
  body: z.string().nullable().default(null), card: MessageCard.nullable().default(null).catch(null),
  created_at: Time, delivered_at: nul(Time), author: nul(z.string()),
  // The owner API turns internal cards (learning, auto_decision, result…) into activity lines and passes the raw card here.
  detail: nul(z.record(z.string(), z.unknown())),
});
export const MessagesResponse = z.object({ messages: z.array(Message) });
export const MessageResponse = z.object({ message: Message });
export const OkResponse = z.looseObject({ ok: z.boolean() });

// ───────── versions & review (§20–§21) ─────────
export const Artifact = z.object({ id: Id, kind: z.string(), filename: z.string(), content_type: z.string(), bytes: z.number().optional(), language: nul(z.string()), meta: z.record(z.string(), z.unknown()).default({}) });
export const Feedback = z.object({
  id: Id, author: z.string(), author_role: z.enum(["owner", "reviewer", "guest"]).default("owner"),
  verdict: FeedbackVerdict, at_seconds: nul(z.number()), note: nul(z.string()),
  scope: z.enum(["version", "episode_all_languages", "series", "category"]).default("version"),
  status: z.enum(["new", "sent_to_studio", "dismissed"]).default("new"), created_at: Time,
});
export const VersionDetail = z.object({
  version: z.object({
    id: Id, episode_id: Id, series_id: Id, series_title: z.string(), episode_sequence: z.number(), episode_title: z.string(),
    episodes_total: nul(z.number()), language_code: z.string(), role: z.enum(["master", "variant"]), version: z.number(),
    status: VersionStatus, origin: z.enum(["studio", "operator_supplied", "imported"]).default("studio"),
    changelog: nul(z.string()), quality_report: QualityReport.nullable().default(null),
    metadata: z.record(z.string(), z.unknown()).default({}), duration_s: nul(z.number()),
    chosen_cover_artifact_id: nul(Id), created_at: Time,
  }),
  artifacts: z.array(Artifact).default([]),
  feedback: z.array(Feedback).default([]),
  history: z.array(VersionRef).default([]),
  decisions: z.array(Decision).default([]),
});
export const FeedbackResponse = z.object({ feedback: Feedback });

// ───────── plan revision & learnings (M1.5) — contract DTOs verbatim ─────────
/** GET/PUT /api/settings/studio — autonomy (owner 2026-09-25: auto mode by default). */
export const StudioSettings = StudioSettingsDto;
export const PlanRevisionResponse = z.object({ work_item_id: Id });
export const Learning = LearningProposalView;
export const LearningsResponse = z.object({ learnings: z.array(LearningProposalView) });

// ───────── publishing (M2) — the contract DTOs verbatim ─────────
export const Publication = PublicationView;
export const PublicationsResponse = z.object({ publications: z.array(PublicationView) });
/** M5a adds the authorization re-check, the YouTube data refresh time and whether the analytics scope was granted (all optional: an older API omits them). */
export const Account = AccountView.extend({
  authorization_verified_at: nul(Time), youtube_data_last_synced_at: nul(Time), analytics_enabled: nul(z.boolean()),
});
export const AccountsResponse = z.object({ accounts: z.array(Account) });
export const Queue = QueueView;
export const QueuesResponse = z.object({ queues: z.array(QueueView) });
export const PublishPlanResult = PublishPlanResponse;
/** GET/PUT /api/settings/platforms/youtube (M2 common brief; not a contract DTO). */
export const YoutubeSettings = z.object({
  api_audited: z.boolean(), default_category_id: z.string(), contains_synthetic_media: z.boolean(),
  playlist_privacy: z.enum(["auto", "public", "unlisted", "private"]).optional(),   // M2.2 series playlists (auto = public once audited)
});
/** M2.2: GET /api/series/:id/playlists and POST …/playlists/sync — contract DTOs verbatim. */
export const SeriesPlaylists = SeriesPlaylistsDto;
export const PlaylistSyncResult = PlaylistSyncResultDto;
/** M2.3: GET/POST /api/versions/:id/covers and PUT /api/versions/:id/cover — contract DTO verbatim. */
export const VersionCovers = VersionCoversDto;
/** M2.4: GET/POST /api/series/:id/covers and PUT /api/series/:id/cover — contract DTO verbatim. */
export const SeriesCovers = SeriesCoversDto;

// ───────── M2.1: post log, hardening, notifications — contract DTOs verbatim ─────────
export const PublicationsPage = PublicationsPageDto;
export const PublicationHistory = PublicationHistoryDto;
export const PublishingSettings = PublishingSettingsDto;
export const AccountImpact = AccountPublishingImpact;
export const RescheduleResult = RescheduleResponse;
/** POST/PUT /api/queues responses: the queue plus advisories (only `warnings` is relied on). */
export const QueueSaveResult = z.looseObject({ warnings: z.array(z.string()).optional() });
export const Notification = NotificationView;
export const NotificationsPage = NotificationList;
export const NotificationSettings = NotificationSettingsDto;
export const PushKey = PushPublicKey;
export const PushTestResult = PushTestResultDto;

// ───────── sharing (M3) ─────────
export const Contact = z.object({ id: Id, name: z.string(), role: nul(z.string()), whatsapp: nul(z.string()), email: nul(z.string()), languages: z.array(z.string()).default([]) });
export const ContactsResponse = z.object({ contacts: z.array(Contact) });
export const ContactResponse = z.object({ contact: Contact });
export const ShareLinkResponse = z.object({ url: z.string(), whatsapp_url: z.string(), expires_at: nul(Time) });

// ───────── engagement (M4) ─────────
export const ReplyDraft = z.object({ id: Id, body: z.string(), body_translation: nul(z.string()), status: z.enum(["draft", "approved", "posting", "posted", "failed", "discarded"]), source: z.enum(["studio", "owner"]).default("studio") });
export const Comment = z.object({
  id: Id, platform: z.string(), author_name: nul(z.string()), text: z.string(), language: nul(z.string()),
  translation: nul(z.string()),
  triage: nul(z.object({ category: z.string().optional(), priority: z.enum(["red", "yellow", "none"]).optional(), reason: z.string().optional() })),
  status: z.enum(["new", "needs_attention", "worth_reviewing", "no_action", "replied", "ignored"]),
  published_at: nul(Time), series_title: nul(z.string()), episode_title: nul(z.string()), platform_url: nul(z.string()),
  draft: nul(ReplyDraft),
});
export const CommentsResponse = z.object({ comments: z.array(Comment) });
export const ReplyResponse = z.object({ reply: ReplyDraft });

// ───────── analytics (M5a: YouTube data + analytics) ─────────
/** A YouTube figure: a number (the Data API's statistics arrive as strings — accepted too), or null when YouTube has none. */
const Figure = z.union([z.number(), z.string().transform((v) => (v.trim() === "" || Number.isNaN(Number(v)) ? null : Number(v)))]).nullable().optional().catch(null);
const FigureSource = z.enum(["youtube", "sum_of_channels"]).catch("youtube");
export const ChannelMetrics = z.looseObject({
  views: Figure, engagedViews: Figure, estimatedMinutesWatched: Figure, averageViewDuration: Figure, averageViewPercentage: Figure,
  likes: Figure, comments: Figure, shares: Figure, subscribersGained: Figure, subscribersLost: Figure,
});
export const AnalyticsAccount = z.object({
  id: Id, name: z.string().default("Channel"), handle: nul(z.string()), channel_id: nul(z.string()), channel_url: nul(z.string()),
  auth_status: z.string().nullable().default(null), analytics_enabled: z.boolean().nullable().default(null),
  last_synced_at: nul(Time), authorization_verified_at: nul(Time), data_through: nul(z.string()),
  last_run_status: z.enum(["ok", "partial", "failed", "running"]).nullable().default(null).catch(null),
});
export const AnalyticsDay = z.object({
  date: z.string(), views: Figure, engagedViews: Figure, estimatedMinutesWatched: Figure, subscribersGained: Figure, subscribersLost: Figure,
});
export const ContentTypeTotals = z.object({ content_type: z.string(), views: Figure, engagedViews: Figure, estimatedMinutesWatched: Figure });
export const TopVideo = z.object({
  account_id: nul(z.string()), video_id: z.string(), title: z.string().default(""), published_at: nul(Time), privacy_status: nul(z.string()),
  url: nul(z.string()), duration_s: Figure,
  metrics: z.looseObject({
    views: Figure, engagedViews: Figure, estimatedMinutesWatched: Figure, averageViewDuration: Figure, averageViewPercentage: Figure,
    likes: Figure, comments: Figure, shares: Figure, subscribersGained: Figure,
  }).default({}),
  statistics: z.object({ viewCount: Figure, likeCount: Figure, commentCount: Figure }).nullable().default(null).catch(null),
  publication_id: nul(z.string()),
});
export const AnalyticsPlaylist = z.object({
  account_id: nul(z.string()), playlist_id: z.string(), title: z.string().default(""), privacy_status: nul(z.string()), item_count: Figure, url: nul(z.string()),
  /** Whatever YouTube returned for the playlist (a metric it rejected is absent); null = no figures yet. */
  metrics: z.record(z.string(), Figure).nullable().default(null).catch(null),
});
/** GET /api/analytics/overview?range&account — every list defaults to empty so a sparse or "never synced" answer renders the empty state. */
export const AnalyticsOverview = z.object({
  range: z.string().default("28d"),
  account: z.string().default("all"),
  window: z.object({ start: z.string(), end: z.string() }).nullable().default(null).catch(null),
  accounts: z.array(AnalyticsAccount).default([]),
  totals: z.object({ source: FigureSource, metrics: ChannelMetrics.default({}) }).default({ source: "youtube", metrics: {} }),
  daily: z.array(AnalyticsDay).default([]),
  daily_source: FigureSource.default("youtube"),
  content_types: z.array(ContentTypeTotals).default([]),
  /** Not in the contract yet: if absent the split follows `totals.source`. */
  content_types_source: FigureSource.optional(),
  top_videos: z.array(TopVideo).default([]),
  playlists: z.array(AnalyticsPlaylist).default([]),
  freshness: z.object({ last_synced_at: nul(Time), data_through: nul(z.string()) }).default({ last_synced_at: null, data_through: null }),
  disclosure: z.string().nullable().default(null),
  // legacy (pre-M5a) fields, still sent by the API
  kpis: z.array(z.object({ key: z.string(), label: z.string(), value: z.number(), unit: z.string().nullable().optional(), delta_pct: nul(z.number()) })).default([]),
});
/** POST /api/analytics/sync → 202 (a 429 carries `retry_at`, surfaced as ApiError.details.retry_at). */
export const AnalyticsSyncResult = z.object({ runs: z.array(z.object({ account_id: z.string(), run_id: z.string() })).default([]) });
export const SyncCall = z.object({
  api: z.string().default("data"), endpoint: z.string().default(""),
  params: z.record(z.string(), z.unknown()).nullable().default({}).catch({}),
  status: Figure, rows: Figure, units: Figure, ms: Figure,
  /** Metrics YouTube rejected and the run dropped (never fails the run). Assumed name; `params.dropped_metrics` is read too. */
  dropped_metrics: z.array(z.string()).optional().catch(undefined),
  error: z.unknown().optional(),
});
export const SyncRun = z.object({
  id: z.string(), account_id: nul(z.string()), started_at: nul(Time), finished_at: nul(Time), status: z.string().default("running"),
  error: z.unknown().optional().transform((e): string | null => (e == null ? null : typeof e === "string" ? e : typeof e === "object" && e && "message" in e ? String((e as { message: unknown }).message) : JSON.stringify(e))),
  calls: z.array(SyncCall).default([]),
});
export const SyncRunsResponse = z.object({ runs: z.array(SyncRun).default([]) });
/** DELETE /api/youtube-data?account= → per-table row counts. */
export const YoutubeDataDeleted = z.object({ deleted: z.record(z.string(), z.number()).default({}) });

// ───────── realtime (§7) ─────────
export const StreamEvent = z.object({ table: z.string(), id: z.union([z.string(), z.number()]).transform(String), kind: z.string(), series_id: nul(z.string()), summary: nul(z.string()) });

export type User = z.infer<typeof User>;
export type Dashboard = z.infer<typeof Dashboard>;
export type AttentionItem = z.infer<typeof AttentionItem>;
export type RunnerStatus = z.infer<typeof RunnerStatus>;
export type Health = z.infer<typeof Health>;
export type Category = z.infer<typeof Category>;
export type Language = z.infer<typeof Language>;
export type SeriesSummary = z.infer<typeof SeriesSummary>;
export type SeriesDetail = z.infer<typeof SeriesDetail>;
export type Decision = z.infer<typeof Decision>;
export type Episode = z.infer<typeof Episode>;
export type VersionRef = z.infer<typeof VersionRef>;
export type PlanRecord = z.infer<typeof PlanRecord>;
export type Message = z.infer<typeof Message>;
export type MessageCard = z.infer<typeof MessageCard>;
export type Artifact = z.infer<typeof Artifact>;
export type Feedback = z.infer<typeof Feedback>;
export type VersionDetail = z.infer<typeof VersionDetail>;
export type Learning = z.infer<typeof Learning>;
export type StudioSettings = z.infer<typeof StudioSettings>;
export type LearningStatus = Learning["status"];
export type Publication = z.infer<typeof Publication>;
export type Account = z.infer<typeof Account>;
export type Queue = z.infer<typeof Queue>;
export type PublishPlanResult = z.infer<typeof PublishPlanResult>;
export type YoutubeSettings = z.infer<typeof YoutubeSettings>;
export type SeriesPlaylists = z.infer<typeof SeriesPlaylists>;
export type SeriesPlaylist = SeriesPlaylists["playlists"][number];
export type PlaylistSyncResult = z.infer<typeof PlaylistSyncResult>;
export type VersionCovers = z.infer<typeof VersionCovers>;
export type CoverView = VersionCovers["covers"][number];
export type SeriesCovers = z.infer<typeof SeriesCovers>;
export type ThumbnailStatus = NonNullable<Publication["thumbnail"]>;
export type Contact = z.infer<typeof Contact>;
export type PublicationsPage = z.infer<typeof PublicationsPage>;
export type PublicationHistory = z.infer<typeof PublicationHistory>;
export type PublishingSettings = z.infer<typeof PublishingSettings>;
export type AccountImpact = z.infer<typeof AccountImpact>;
export type RescheduleResult = z.infer<typeof RescheduleResult>;
export type Notification = z.infer<typeof Notification>;
export type NotificationsPage = z.infer<typeof NotificationsPage>;
export type NotificationSettings = z.infer<typeof NotificationSettings>;
export type PushTestResult = z.infer<typeof PushTestResult>;
export type ShareLink = z.infer<typeof ShareLinkResponse>;
export type Comment = z.infer<typeof Comment>;
export type ReplyDraft = z.infer<typeof ReplyDraft>;
export type AnalyticsOverview = z.infer<typeof AnalyticsOverview>;
export type AnalyticsAccount = z.infer<typeof AnalyticsAccount>;
export type ChannelMetrics = z.infer<typeof ChannelMetrics>;
export type TopVideo = z.infer<typeof TopVideo>;
export type AnalyticsPlaylist = z.infer<typeof AnalyticsPlaylist>;
export type AnalyticsSyncResult = z.infer<typeof AnalyticsSyncResult>;
export type SyncRun = z.infer<typeof SyncRun>;
export type SyncCall = z.infer<typeof SyncCall>;
export type YoutubeDataDeleted = z.infer<typeof YoutubeDataDeleted>;
export type StreamEvent = z.infer<typeof StreamEvent>;
export type SeriesStatus = z.infer<typeof SeriesStatus>;
export type VersionStatus = z.infer<typeof VersionStatus>;
